// Feature Flags: single source of truth
// -------------------------------------------------------------
// Policy
// - Define and read ALL feature flags in this module only.
// - App code must import flags from here; do NOT access import.meta.env directly.
// - Tests should mock this module (e.g., jest.mock('src/config/featureFlags', ...))
//   rather than attempting to patch import.meta.env.
// - Add new flags by updating:
//   1) vite-env.d.ts (ImportMetaEnv typing),
//   2) the Vite .env files (e.g., .env, .env.local, CI env), and
//   3) an exported constant below using toBool or a parser as needed.
// Implementation
// - Uses Vite env variables when available; falls back to false in Jest/Node.
type ViteEnv = {
  VITE_ENABLE_AI?: string | boolean;
  VITE_FEATURE_AI_PLANNER?: string | boolean;
  VITE_FEATURE_FORMS_FINDER?: string | boolean;
  VITE_FEATURE_PUBLIC_GUIDE_BROWSE?: string | boolean;
  VITE_LEGAL_HELP_URL?: string;
  // WO-ADMIN-001 Phase 6.2 — Firestore-backed jurisdiction content (cutover flags)
  // Set to 'true' in .env.local after verifying jurisdictionLinks / jurisdictionGuides
  // collections are populated for all production jurisdictions.
  VITE_USE_FIRESTORE_JURISDICTION_LINKS?: string | boolean;
  VITE_USE_FIRESTORE_JURISDICTION_GUIDES?: string | boolean;
  PROD?: boolean;
  // Allow unknown additional keys without using any
  [key: string]: unknown;
};

const viteEnv: ViteEnv =
  (typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: ViteEnv }).env) ||
  {};

const toBool = (v: unknown): boolean => v === true || v === 'true';

export const ENABLE_AI = toBool(viteEnv.VITE_ENABLE_AI);

// Specific flag for the Filing Planner AI entry point, backed by Vite env.
export const ENABLE_AI_PLANNER = toBool(viteEnv.VITE_FEATURE_AI_PLANNER);

// Forms Finder feature flag
export const ENABLE_FORMS_FINDER = toBool(viteEnv.VITE_FEATURE_FORMS_FINDER);

// Public guide browsing - allows SRLs to browse guides without a case context
export const ENABLE_PUBLIC_GUIDE_BROWSE = toBool(viteEnv.VITE_FEATURE_PUBLIC_GUIDE_BROWSE);

// Legal Help URL configuration
export const LEGAL_HELP_URL = typeof viteEnv.VITE_LEGAL_HELP_URL === 'string' 
  ? viteEnv.VITE_LEGAL_HELP_URL 
  : 'https://www.lsc.gov/what-legal-aid/find-legal-aid';

// WO-ADMIN-001 Phase 6.2 — Firestore jurisdiction content cutover flags.
// Off by default. Enable in .env.local or deployment env once collections are seeded.
export const USE_FIRESTORE_JURISDICTION_LINKS = toBool(viteEnv.VITE_USE_FIRESTORE_JURISDICTION_LINKS);
export const USE_FIRESTORE_JURISDICTION_GUIDES = toBool(viteEnv.VITE_USE_FIRESTORE_JURISDICTION_GUIDES);

// Convenience boolean for production check. This module is the one allowed to read import.meta.env.
export const IS_PROD: boolean = !!(typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD);

// Convenience boolean for development check.
export const IS_DEV: boolean = !!(typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);

// Convenience boolean for preview mode check.
export const IS_PREVIEW: boolean = (() => {
  const env = (typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: Record<string, unknown> }).env) || {};
  return env.VITE_PREVIEW === 'true';
})();

// Helpers for other modules to read typed flags via this central module
export const getFlagBoolean = (name: string, defaultValue = false): boolean => {
  const env = (typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: Record<string, unknown> }).env) || {};
  const v = env[name];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v === 'true';
  return defaultValue;
};

export const getFlagNumber = (name: string, defaultValue: number): number => {
  const env = (typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: Record<string, unknown> }).env) || {};
  const v = env[name];
  const n = typeof v === 'number' ? v : parseInt(typeof v === 'string' ? v : '', 10);
  return Number.isFinite(n) ? n : defaultValue;
};

// String accessor for selected settings (e.g., app version). Keep usage minimal.
export const getEnvString = (name: string, defaultValue = ''): string => {
  const env = (typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: Record<string, unknown> }).env) || {};
  const v = env[name];
  return typeof v === 'string' && v ? v : defaultValue;
};

// ============================================================================
// AI FEATURES V2 FLAGS
// ============================================================================

/**
 * AI Features V2 Flags
 * 
 * Each AI feature can be independently enabled/disabled.
 * These flags control UI visibility and feature availability.
 * 
 * To disable a problematic feature without code changes,
 * set the corresponding flag to false.
 */
export const AI_FEATURES_V2 = {
  /** Journal entry auto-tagging and category suggestions */
  journalTagging: true,

  /** Incident extraction from journal, messages, inbox */
  incidentExtraction: true,

  /** AI-generated declaration drafts */
  declarationDraft: true,

  /** AI-generated letter drafts */
  letterDraft: true,

  /** AI-powered form selection recommendations */
  formSelection: true,

  /** AI assistance for form field completion */
  formFieldAssist: true,

  /** Evidence gap analysis and suggestions */
  evidenceGapAnalysis: true,
} as const;

/**
 * Check if an AI v2 feature is enabled
 * 
 * @param feature - AI feature name
 * @returns boolean indicating if feature is enabled
 */
export function isAiFeatureEnabled(feature: keyof typeof AI_FEATURES_V2): boolean {
  return AI_FEATURES_V2[feature] ?? false;
}

/**
 * Get all enabled AI v2 features
 * 
 * @returns Array of enabled AI feature names
 */
export function getEnabledAiFeatures(): string[] {
  return Object.entries(AI_FEATURES_V2)
    .filter(([_, enabled]) => enabled)
    .map(([feature]) => feature);
}
